import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	input,
	output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { negativeHeader$ } from '@valk-nx/core/lib/core';
import { BaseDirective } from '@valk-nx/core/lib/directives/base/base.directive';

import { HeaderDesktopComponent } from './header-desktop/header-desktop';
import { HeaderMobileComponent } from './header-mobile/header-mobile';
import {
	CtaGroupResolved,
	CtaItem,
	HeaderMenuItem,
	isCtaGroup,
	MetaMenu,
} from './header.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-header`,
	templateUrl: './header.component.html',
	imports: [CommonModule, HeaderDesktopComponent, HeaderMobileComponent],
})
export class HeaderComponent extends BaseDirective {
	private readonly cd = inject(ChangeDetectorRef);

	readonly title = input.required<string>();
	readonly menuItems = input<HeaderMenuItem[]>([]);
	readonly metaMenu = input<MetaMenu>();
	readonly hideMenu = input<boolean>(false);

	readonly languageSwitch = output<string>();

	readonly transformedTitle = computed(() =>
		this.title().toLowerCase().trim().replaceAll(/\s+/g, '-'),
	);

	readonly resolvedCta = computed<CtaItem | CtaGroupResolved | undefined>(
		() => {
			const cta = this.metaMenu()?.cta;

			if (!cta) return undefined;

			if (!isCtaGroup(cta)) return cta;

			return {
				title: cta.title,
				items: cta.items.map((item) => ({
					text: item.title,
					url: item.url,
					targetSelf: item.target !== '_blank',
				})),
			};
		},
	);

	isNegativeHeader = false;

	constructor() {
		super();

		negativeHeader$.pipe(takeUntilDestroyed()).subscribe((isNegative) => {
			this.isNegativeHeader = isNegative;
			this.cd.markForCheck();
		});
	}

	onLanguageSwitch(language: string) {
		this.languageSwitch.emit(language);
	}
}
